import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Chart, ChartConfiguration, DoughnutController, ArcElement, Tooltip, Legend } from 'chart.js';
import { PieChart } from 'lucide-react';

Chart.register(DoughnutController, ArcElement, Tooltip, Legend);

interface SpendingData {
  category: string;
  amount: number;
}

interface SpendingChartProps {
  data: SpendingData[];
  isLoading?: boolean;
}

const CHART_COLORS = [
  '#3E63DD', // primary
  '#22C55E', // green
  '#F59E0B', // amber
  '#EF4444', // red
  '#8B5CF6', // purple
  '#06B6D4', // cyan
  '#EC4899', // pink
  '#F97316', // orange
];

export default function SpendingChart({ data, isLoading }: SpendingChartProps) {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstanceRef = useRef<Chart | null>(null);

  useEffect(() => {
    if (!chartRef.current || isLoading || data.length === 0) return;

    // Destroy existing chart
    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
    }

    const ctx = chartRef.current.getContext('2d');
    if (!ctx) return;

    const total = data.reduce((sum, d) => sum + d.amount, 0);

    const config: ChartConfiguration<'doughnut'> = {
      type: 'doughnut',
      data: {
        labels: data.map((d) => d.category),
        datasets: [
          {
            data: data.map((d) => d.amount),
            backgroundColor: CHART_COLORS.slice(0, data.length),
            borderWidth: 2,
            borderColor: '#141416',
            hoverBorderWidth: 2,
            hoverBorderColor: '#0C0C0D',
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '68%',
        animation: {
          duration: 700,
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            backgroundColor: '#141416',
            titleColor: '#FFFFFF',
            bodyColor: '#94969C',
            borderColor: '#1F2123',
            borderWidth: 1,
            displayColors: false,
            callbacks: {
              label: (context) => {
                const value = context.parsed;
                const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
                return `${formatCurrency(value)} (${percentage}%)`;
              },
            },
          },
        },
      },
    };

    chartInstanceRef.current = new Chart(ctx, config);

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }
    };
  }, [data, isLoading]);

  const total = data.reduce((sum, d) => sum + d.amount, 0);

  if (isLoading) {
    return (
      <div className="card">
        <div className="flex items-center justify-between pb-5 border-b border-border">
          <div>
            <h3 className="text-lg font-semibold text-text-header">Spending by Category</h3>
            <p className="text-sm text-text-muted">This month</p>
          </div>
        </div>
        <div className="pt-6">
          <div className="animate-pulse flex flex-col lg:flex-row items-center gap-8">
            <div className="w-56 h-56 rounded-full bg-background-subtle border border-border" />
            <div className="flex-1 w-full grid grid-cols-2 gap-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-4 bg-background-subtle rounded" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="card">
        <div className="flex items-center justify-between pb-5 border-b border-border">
          <div>
            <h3 className="text-lg font-semibold text-text-header">Spending by Category</h3>
            <p className="text-sm text-text-muted">This month</p>
          </div>
        </div>
        <div className="pt-10 pb-8 text-center">
          <div className="mx-auto h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center mb-4 border border-primary/20">
            <PieChart className="h-6 w-6 text-primary" />
          </div>
          <p className="text-sm text-text-muted">No spending data this month.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between pb-5 border-b border-border">
        <div>
          <h3 className="text-lg font-semibold text-text-header">Spending by Category</h3>
          <p className="text-sm text-text-muted">This month</p>
        </div>
        <Link
          to="/transactions"
          className="text-sm font-medium text-primary hover:text-primary-hover transition-colors"
        >
          View all
        </Link>
      </div>

      <div className="pt-6">
        <div className="flex flex-col lg:flex-row items-center gap-8">
          {/* Chart */}
          <div className="relative w-56 h-56 flex-shrink-0">
            <canvas ref={chartRef} />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <p className="text-xs text-text-muted">Total</p>
                <p className="text-lg font-semibold text-text-header">{formatCurrency(total)}</p>
              </div>
            </div>
          </div>

          {/* Legend */}
          <div className="flex-1 w-full">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
              {data.map((item, index) => {
                const percentage = total > 0 ? (item.amount / total) * 100 : 0;
                const color = CHART_COLORS[index % CHART_COLORS.length];
                return (
                  <div
                    key={item.category}
                    className="flex items-center justify-between gap-3 py-2 px-3 rounded-lg border border-transparent hover:border-border hover:bg-background-subtle/40 transition-all duration-200"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: color }}
                      />
                      <span className="text-sm text-text-body truncate">{item.category}</span>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-medium text-text-header">{formatCurrency(item.amount)}</p>
                      <p className="text-xs text-text-muted">{percentage.toFixed(1)}%</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}
